/**
 * Routing Engine Service
 * Comprehensive routing with Google Maps integration
 * Features: Route optimization, ETAs with dwell times, live traffic, multi-load routing
 */

import { supabase } from '../supabaseClient';
import { logLoadActivity } from './activityLogService';   // __BOARD_ACTIVITY__
import { parseDbTime, fmtDbDateTime, parseWindowTime } from '../utils/dbTime';
import { getCachedSharedMapsKey } from './mapsKeyService';

const ROUTING_CONFIG_KEY = 'hmgc_routing_config';

const defaultConfig = {
  googleMapsApiKey: '',
  defaultDwellMinutes: 30,
  trafficModel: 'best_guess',
  avoidTolls: false,
  avoidHighways: false,
  optimizeWaypoints: true,
  units: 'imperial',
  region: '',
  homeLocation: '',
  homeLat: null,
  homeLng: null,
  homeRadius: 50,
  // Daily plan baseline: trucks leave the yard (homeLocation) at this local
  // time. The first stop's ETA = dayStartTime + drive from the yard, not
  // dayStartTime itself (the old plan assumed the truck was already at stop 1
  // at 07:00). Set startFromYard=false to keep the old behaviour.
  dayStartTime: '07:00',
  startFromYard: true,
};

// __BOARD_MAPS_KEY__ the Google key comes from ONE shared place (mapsKeyService:
// app_config 'board_google_maps_key', cached locally) and falls back to this
// account's own key. getLocalRoutingConfig() is the raw per-user config (the
// Settings form edits that one, so the shared key is never copied into it).
export const getLocalRoutingConfig = () => {
  try {
    const config = localStorage.getItem(ROUTING_CONFIG_KEY);
    return config ? { ...defaultConfig, ...JSON.parse(config) } : { ...defaultConfig };
  } catch (error) {
    console.error('Error reading routing config:', error);
    return { ...defaultConfig };
  }
};

export const getRoutingConfig = () => {
  const cfg = getLocalRoutingConfig();
  const shared = getCachedSharedMapsKey();
  if (shared) cfg.googleMapsApiKey = shared;
  return cfg;
};

export const saveRoutingConfig = (config) => {
  try {
    localStorage.setItem(ROUTING_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (error) {
    console.error('Error saving routing config:', error);
    return false;
  }
};

export const loadRoutingConfigFromDatabase = async (userId) => {
  if (!userId) return null;
  
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('settings')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error('Error loading routing config from database:', error);
      return null;
    }
    
    if (data?.settings?.routingConfig) {
      const config = { ...defaultConfig, ...data.settings.routingConfig };
      localStorage.setItem(ROUTING_CONFIG_KEY, JSON.stringify(config));
      return config;
    }
    
    return null;
  } catch (error) {
    console.error('Error loading routing config from database:', error);
    return null;
  }
};

export const saveRoutingConfigToDatabase = async (userId, config) => {
  if (!userId) return false;
  
  try {
    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('settings')
      .eq('id', userId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching profile for settings update:', fetchError);
      return false;
    }
    
    const existingSettings = existingProfile?.settings || {};
    const updatedSettings = {
      ...existingSettings,
      routingConfig: config
    };
    
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ settings: updatedSettings })
      .eq('id', userId);
    
    if (updateError) {
      console.error('Error saving routing config to database:', updateError);
      return false;
    }
    
    localStorage.setItem(ROUTING_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (error) {
    console.error('Error saving routing config to database:', error);
    return false;
  }
};

export const isGoogleMapsConfigured = () => {
  const config = getRoutingConfig();
  return !!config.googleMapsApiKey;
};

/**
 * Geocode an address using Google Maps Geocoding API
 */
export const geocodeAddress = async (address) => {
  const config = getRoutingConfig();
  
  if (!address) return null;

  // Use Google Maps JavaScript API Geocoder (no CORS issues)
  if (!window.google?.maps?.Geocoder || !config.googleMapsApiKey) {
    return simulateGeocode(address);
  }

  try {
    const geocoder = new window.google.maps.Geocoder();
    
    const result = await new Promise((resolve, reject) => {
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results.length > 0) {
          resolve(results[0]);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });

    return {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
      formattedAddress: result.formatted_address,
      placeId: result.place_id,
      confidence: 1.0,
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return simulateGeocode(address);
  }
};

const simulateGeocode = (address) => {
  const addressLower = address.toLowerCase();
  const cities = {
    'toronto': { lat: 43.6532, lng: -79.3832 },
    'mississauga': { lat: 43.5890, lng: -79.6441 },
    'brampton': { lat: 43.7315, lng: -79.7624 },
    'hamilton': { lat: 43.2557, lng: -79.8711 },
    'ottawa': { lat: 45.4215, lng: -75.6972 },
    'london': { lat: 42.9849, lng: -81.2453 },
    'kitchener': { lat: 43.4516, lng: -80.4925 },
    'windsor': { lat: 42.3149, lng: -83.0364 },
    'barrie': { lat: 44.3894, lng: -79.6903 },
    'kingston': { lat: 44.2312, lng: -76.4860 },
  };

  for (const [city, coords] of Object.entries(cities)) {
    if (addressLower.includes(city)) {
      return {
        lat: coords.lat + (Math.random() - 0.5) * 0.02,
        lng: coords.lng + (Math.random() - 0.5) * 0.02,
        formattedAddress: address,
        confidence: 0.85,
      };
    }
  }

  return {
    lat: 43.6532 + (Math.random() - 0.5) * 2,
    lng: -79.3832 + (Math.random() - 0.5) * 2,
    formattedAddress: address,
    confidence: 0.5,
  };
};

/**
 * Get route between multiple stops using Google Maps Directions API
 * Supports optimization, traffic, and turn-by-turn directions
 */
export const calculateRoute = async (stops, options = {}) => {
  const config = getRoutingConfig();
  const {
    optimize = config.optimizeWaypoints,
    departureTime = null,
    trafficModel = config.trafficModel,
  } = options;

  if (stops.length < 2) {
    return { error: 'At least 2 stops required' };
  }

  // Use Google Maps JavaScript API DirectionsService (no CORS issues)
  if (!window.google?.maps?.DirectionsService || !config.googleMapsApiKey) {
    return simulateRoute(stops, options);
  }

  try {
    const directionsService = new window.google.maps.DirectionsService();

    const origin = stops[0].address || `${stops[0].lat},${stops[0].lng}`;
    const destination = stops[stops.length - 1].address || `${stops[stops.length - 1].lat},${stops[stops.length - 1].lng}`;
    const waypoints = stops.slice(1, -1).map(s => ({
      location: s.address || `${s.lat},${s.lng}`,
      stopover: true,
    }));

    const request = {
      origin: origin,
      destination: destination,
      waypoints: waypoints,
      optimizeWaypoints: optimize && waypoints.length > 0,
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.METRIC,
    };

    if (config.avoidTolls || config.avoidHighways) {
      request.avoidTolls = config.avoidTolls || false;
      request.avoidHighways = config.avoidHighways || false;
    }

    if (departureTime) {
      request.drivingOptions = {
        departureTime: new Date(departureTime),
        trafficModel: trafficModel === 'best_guess' 
          ? window.google.maps.TrafficModel.BEST_GUESS 
          : trafficModel === 'pessimistic'
            ? window.google.maps.TrafficModel.PESSIMISTIC
            : window.google.maps.TrafficModel.OPTIMISTIC,
      };
    }

    const result = await new Promise((resolve, reject) => {
      directionsService.route(request, (response, status) => {
        if (status === 'OK') {
          resolve(response);
        } else {
          reject(new Error(`Directions request failed: ${status}`));
        }
      });
    });

    const route = result.routes[0];
    const legs = route.legs.map((leg, index) => ({
      legSequence: index + 1,
      originAddress: leg.start_address,
      originLat: leg.start_location.lat(),
      originLng: leg.start_location.lng(),
      destinationAddress: leg.end_address,
      destinationLat: leg.end_location.lat(),
      destinationLng: leg.end_location.lng(),
      distanceMeters: leg.distance.value,
      distanceText: leg.distance.text,
      durationSeconds: leg.duration.value,
      durationText: leg.duration.text,
      durationInTrafficSeconds: leg.duration_in_traffic?.value || leg.duration.value,
      durationInTrafficText: leg.duration_in_traffic?.text || leg.duration.text,
      polyline: leg.steps.map(s => s.polyline?.points).filter(Boolean).join(''),
    }));

    const totalDistanceMeters = legs.reduce((sum, leg) => sum + leg.distanceMeters, 0);
    const totalDurationSeconds = legs.reduce((sum, leg) => sum + leg.durationSeconds, 0);
    const totalDurationInTrafficSeconds = legs.reduce((sum, leg) => sum + leg.durationInTrafficSeconds, 0);

    return {
      success: true,
      legs,
      totalDistanceMeters,
      totalDistanceKm: totalDistanceMeters / 1000,
      totalDistanceMiles: totalDistanceMeters / 1609.34,
      totalDurationSeconds,
      totalDurationInTrafficSeconds,
      totalDurationText: formatDuration(totalDurationSeconds),
      totalDurationInTrafficText: formatDuration(totalDurationInTrafficSeconds),
      polyline: route.overview_polyline,
      waypointOrder: route.waypoint_order || [],
      isOptimized: optimize && waypoints.length > 0,
      directionsResult: result,
    };
  } catch (error) {
    console.error('Route calculation error:', error);
    return simulateRoute(stops, options);
  }
};

const simulateRoute = (stops, options = {}) => {
  const legs = [];
  let totalDistanceMeters = 0;
  let totalDurationSeconds = 0;

  for (let i = 0; i < stops.length - 1; i++) {
    const origin = stops[i];
    const destination = stops[i + 1];
    
    const distanceKm = calculateHaversineDistance(
      origin.lat || 43.6532,
      origin.lng || -79.3832,
      destination.lat || 43.6532 + (Math.random() - 0.5) * 0.5,
      destination.lng || -79.3832 + (Math.random() - 0.5) * 0.5
    );
    
    const distanceMeters = Math.round(distanceKm * 1000 * 1.3);
    const durationSeconds = Math.round((distanceKm / 60) * 3600 * 1.2);

    legs.push({
      legSequence: i + 1,
      originAddress: origin.address,
      originLat: origin.lat,
      originLng: origin.lng,
      destinationAddress: destination.address,
      destinationLat: destination.lat,
      destinationLng: destination.lng,
      distanceMeters,
      distanceText: `${(distanceMeters / 1000).toFixed(1)} km`,
      durationSeconds,
      durationText: formatDuration(durationSeconds),
      durationInTrafficSeconds: Math.round(durationSeconds * 1.15),
      durationInTrafficText: formatDuration(Math.round(durationSeconds * 1.15)),
      turnByTurnInstructions: [
        { instruction: `Head toward ${destination.address}`, distanceMeters: distanceMeters, maneuver: 'straight' },
      ],
    });

    totalDistanceMeters += distanceMeters;
    totalDurationSeconds += durationSeconds;
  }

  return {
    success: true,
    legs,
    totalDistanceMeters,
    totalDistanceKm: totalDistanceMeters / 1000,
    totalDistanceMiles: totalDistanceMeters / 1609.34,
    totalDurationSeconds,
    totalDurationInTrafficSeconds: Math.round(totalDurationSeconds * 1.15),
    totalDurationText: formatDuration(totalDurationSeconds),
    totalDurationInTrafficText: formatDuration(Math.round(totalDurationSeconds * 1.15)),
    isOptimized: false,
    isSimulated: true,
  };
};

const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const calculateOptimizationSavings = (originalStops, optimizedOrder) => {
  return Math.floor(Math.random() * 600) + 120;
};

const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
};

/**
 * Calculate ETAs for all stops including dwell times
 */
export const calculateETAsWithDwellTimes = async (stops, startTime = new Date(), existingRoute = null) => {
  const route = existingRoute || await calculateRoute(stops);
  if (!route.success) return route;

  let currentTime = new Date(startTime);
  const stopsWithETAs = [];

  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i];

    // __PLAN_BASELINE__ legSeconds = the drive INTO this stop (traffic-aware when
    // Google returned duration_in_traffic); the caller sets stop 1's (from the yard).
    let legSeconds = 0;
    if (i > 0) {
      const leg = route.legs[i - 1];
      legSeconds = leg ? (leg.durationInTrafficSeconds || leg.durationSeconds || 0) : 0;
      currentTime = new Date(currentTime.getTime() + (legSeconds * 1000));
    }

    const arrivalTime = new Date(currentTime);
    const dwell = await getDwellTimeForStop(stop);
    const dwellMinutes = dwell.minutes;
    const departureTime = new Date(currentTime.getTime() + (dwellMinutes * 60 * 1000));

    stopsWithETAs.push({
      ...stop,
      stopSequence: i + 1,
      estimatedArrival: arrivalTime.toISOString(),
      estimatedDeparture: departureTime.toISOString(),
      dwellMinutes,
      dwellSource: dwell.source,
      dwellReason: dwell.reason,
      legSeconds,
      arrivalFormatted: formatTime(arrivalTime),
      departureFormatted: formatTime(departureTime),
      isWithinTimeWindow: checkTimeWindow(arrivalTime, stop.time_window_start, stop.time_window_end),
    });

    currentTime = departureTime;
  }

  return {
    success: true,
    stops: stopsWithETAs,
    route,
    totalDwellMinutes: stopsWithETAs.reduce((sum, s) => sum + s.dwellMinutes, 0),
    estimatedStartTime: stopsWithETAs[0]?.estimatedArrival,
    estimatedEndTime: stopsWithETAs[stopsWithETAs.length - 1]?.estimatedDeparture,
  };
};

// Dwell for a stop now comes from ONE resolver (services/dwellService.js ->
// dwell_time_settings, learned from real visits). This used to return
// stop.preset_dwell_minutes first, which is 30 on every load_stops row in the
// database (column default, never set), so every plan allowed a flat 30 minutes
// per stop regardless of the location. Dynamic import: dwellService reads the
// routing config from this module, so a static import would be a cycle.
// __PLAN_BASELINE__ returns {minutes, source, reason} so the plan can say WHY
// (stop override / learned / cargo type / size tier / place / type / default).
const getDwellTimeForStop = async (stop) => {
  try {
    const { resolveStopDwell } = await import('./dwellService');
    const r = await resolveStopDwell(stop);
    if (r && r.minutes) return r;
  } catch (error) {
    console.error('dwell resolve failed, falling back:', error);
  }

  const config = getRoutingConfig();
  return { minutes: config.defaultDwellMinutes || 30, source: 'config', reason: 'routing default' };
};

// __PLAN_BASELINE__ the hub's start prep (Settings -> Job Duration -> Start drive
// prep, app_settings start_drive_prep_min) - minutes before the truck rolls.
const getStartPrepMinutes = async () => {
  try {
    const { loadStopTimeSettings } = await import('./dwellService');
    const st = await loadStopTimeSettings();
    if (st && Number.isFinite(st.startPrepMin)) return st.startPrepMin;
  } catch (error) {
    console.error('start prep resolve failed, using 10:', error);
  }
  return 10;
};

// A zero-leg "route" for a day with a single routable stop and no yard: Google
// needs two points, but the stop still gets a planned arrival/departure.
const singleStopRoute = () => ({
  success: true, legs: [], totalDistanceMeters: 0, totalDistanceKm: 0, totalDistanceMiles: 0,
  totalDurationSeconds: 0, totalDurationInTrafficSeconds: 0,
  totalDurationText: formatDuration(0), totalDurationInTrafficText: formatDuration(0),
  waypointOrder: [], isOptimized: false,
});

const formatTime = (date) => {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const checkTimeWindow = (arrivalTime, windowStart, windowEnd) => {
  if (!windowStart && !windowEnd) return { status: 'no_window', message: 'No time window set' };
  
  // arrivalTime is an instant the engine computed (parseDbTime leaves a Date or an
  // offset-carrying string alone). __TZ_WINDOWS__ windowStart/windowEnd come straight
  // off load_stops and are Toronto WALL CLOCK (as the hub writes them), so they go
  // through parseWindowTime -- read as UTC they were 4-5 hours early.
  const arrival = parseDbTime(arrivalTime);
  if (!arrival) return { status: 'no_window', message: 'No arrival time' };

  if (windowStart) {
    const start = parseWindowTime(windowStart);
    if (start && arrival < start) {
      const earlyMinutes = Math.round((start - arrival) / 60000);
      return { status: 'early', message: `${earlyMinutes} min early`, earlyMinutes };
    }
  }
  
  if (windowEnd) {
    const end = parseWindowTime(windowEnd);
    if (end && arrival > end) {
      const lateMinutes = Math.round((arrival - end) / 60000);
      return { status: 'late', message: `${lateMinutes} min late`, lateMinutes };
    }
  }
  
  return { status: 'on_time', message: 'Within time window' };
};

/**
 * Calculate optimized route for a driver's entire day (multiple loads)
 */
export const calculateDriverDailyRoute = async (driverId, date, opts = {}) => {
  if (!date) date = new Date().toISOString().split('T')[0];
  let failedStops = [];
  try {
    // __ROUTE_DRAFTS__ opts.loadsOverride: plan exactly these loads (full rows with
    // load_stops) instead of the driver's - a draft route's timeline, no driver needed.
    let fetchedLoads;
    if (opts && Array.isArray(opts.loadsOverride)) {
      fetchedLoads = opts.loadsOverride;
    } else {
      const query = supabase
        .from('loads')
        .select(`
          *,
          load_stops (*)
        `)
        .eq('driver_id', driverId)
        .not('status', 'in', '("complete","cancelled","ready_to_bill","billed","archived")');

      const { data, error } = await query;

      if (error) throw error;
      fetchedLoads = data;
    }
    // __SWAP_SIMULATE__ optional what-if adjustments (dry-run preview): drop/add loads
    // without touching the DB. With no opts this is byte-identical to before.
    let loads = fetchedLoads || [];
    if (opts && Array.isArray(opts.removeLoadIds) && opts.removeLoadIds.length) {
      loads = loads.filter((l) => !opts.removeLoadIds.includes(l.id));
    }
    if (opts && Array.isArray(opts.extraLoads) && opts.extraLoads.length) {
      const have = new Set(loads.map((l) => l.id));
      loads = loads.slice();
      opts.extraLoads.forEach((l) => { if (l && l.id && !have.has(l.id)) { loads.push(l); have.add(l.id); } });
    }
    if (!loads || loads.length === 0) {
      return { success: false, message: 'No loads found for this driver' };
    }

    const allStops = [];
    const loadStopMapping = {};

    for (const load of loads) {
      const sortedStops = (load.load_stops || []).sort((a, b) => {
        if (a.stop_type === 'pickup' && b.stop_type === 'delivery') return -1;
        if (a.stop_type === 'delivery' && b.stop_type === 'pickup') return 1;
        return a.stop_sequence - b.stop_sequence;
      });

      for (const stop of sortedStops) {
        let latitude = stop.latitude;
        let longitude = stop.longitude;
        let geocodeFailed = false;
        
        if ((!latitude || !longitude) && stop.address) {
          const geocoded = await geocodeAddress(stop.address);
          if (geocoded && geocoded.confidence >= 0.8) {
            latitude = geocoded.lat;
            longitude = geocoded.lng;
          } else {
            geocodeFailed = true;
          }
        } else if (!stop.address) {
          geocodeFailed = true;
        }

        if (geocodeFailed) {
          if (!failedStops) failedStops = [];
          failedStops.push({
            address: stop.address || '(no address)',
            stopType: stop.stop_type,
            loadNumber: load.load_number,
            loadId: load.id,
          });
          continue; // Skip this stop from the route
        }
        
        const stopWithLoad = {
          ...stop,
          latitude,
          longitude,
          loadId: load.id,
          loadNumber: load.load_number,
          commodity: load.commodity,
          // __PLAN_BASELINE__ cargo for the dwell resolver (cargo-type time, size
          // tier). The loads columns' names lie: *_cm hold FEET, weight_kg POUNDS.
          cargo_type: load.commodity || null,
          weight_lb: load.weight_kg,
          length_ft: load.length_cm,
          width_ft: load.width_cm,
          height_ft: load.height_cm,
        };
        allStops.push(stopWithLoad);
        loadStopMapping[stop.id] = load.id;
      }
    }

    // __PLAN_BASELINE__ a single routable stop is still a plan (a one-job day):
    // from the yard it is a normal one-leg route, without a yard a zero-leg one.
    if (allStops.length < 1) {
      return { success: false, message: 'Not enough routable stops to calculate route', failedStops: failedStops || [] };
    }

    const config = getRoutingConfig();

    // Baseline start: the configured day start on the plan date (local time).
    // `new Date('YYYY-MM-DD')` is UTC midnight, so build the local date explicitly.
    const [dy, dm, dd] = String(date).split('-').map((x) => parseInt(x, 10));
    const [sh, sm] = String(config.dayStartTime || '07:00').split(':').map((x) => parseInt(x, 10));
    let startTime = new Date(dy, (dm || 1) - 1, dd || 1, isNaN(sh) ? 7 : sh, isNaN(sm) ? 0 : sm, 0, 0);
    // Re-base: planning TODAY after the day start, with nothing started yet, the
    // truck is still at the yard NOW - ETAs from 07:00 would already be stale.
    // (Mid-route re-planning stays on the baseline; live ETAs cover that case.)
    const today = new Date();
    const isToday = today.getFullYear() === dy && (today.getMonth() + 1) === dm && today.getDate() === dd;
    const anyStarted = loads.some((l) => l.started_at || ['en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'].includes(l.status));
    if (isToday && !anyStarted && today > startTime) startTime = new Date(today.getTime());
    // __PLAN_BASELINE__ start prep (hub Job Duration "Start drive prep") comes
    // before the truck leaves: stop 1 = start + prep + drive from the yard.
    const prepMinutes = await getStartPrepMinutes();
    startTime = new Date(startTime.getTime() + prepMinutes * 60000);

    // Start from the yard when one is configured: prepend it as a fixed origin
    // so stop 1 gets a real travel leg (waypoint optimisation keeps origin and
    // final destination fixed, so this doesn't disturb the ordering logic).
    const yardOrigin = (config.startFromYard !== false && config.homeLat != null && config.homeLng != null)
      ? { id: '__yard__', stop_type: 'yard', address: `${config.homeLat},${config.homeLng}`, label: config.homeLocation || 'Yard', lat: config.homeLat, lng: config.homeLng, latitude: config.homeLat, longitude: config.homeLng }
      : null;
    // __ROUTE_DRAFTS__ opts.stopOrder: keep this exact stop order (a draft route the
    // office arranged or the Route Builder optimiser chose, pickups before their
    // drop-offs); stops it does not name follow in the usual order. No Google
    // re-ordering then - Google's optimiser does not know a pickup must come first.
    const fixedOrder = !!(opts && Array.isArray(opts.stopOrder) && opts.stopOrder.length);
    if (fixedOrder) {
      const pos = new Map(opts.stopOrder.map((id, i) => [id, i]));
      const named = allStops.filter((st) => pos.has(st.id)).sort((a, b) => pos.get(a.id) - pos.get(b.id));
      const rest = allStops.filter((st) => !pos.has(st.id));
      allStops.splice(0, allStops.length, ...named, ...rest);
    }
    const routeInput = yardOrigin ? [yardOrigin, ...allStops] : allStops;
    // __PLAN_BASELINE__ ask Google for traffic-aware legs (duration_in_traffic) by
    // giving the real departure time; Google rejects a departure in the past, so a
    // plan for an earlier day is routed without one.
    const nowMs = Date.now();
    const departAt = startTime.getTime() >= nowMs - 15 * 60000 ? new Date(Math.max(startTime.getTime(), nowMs + 60000)) : null;
    const routeResult = routeInput.length < 2
      ? singleStopRoute()
      : await calculateRoute(routeInput, { optimize: fixedOrder ? false : config.optimizeWaypoints, departureTime: departAt });

    if (!routeResult.success) {
      return routeResult;
    }

    let reorderedStops = routeResult.isOptimized ? reorderStops(routeInput, routeResult.waypointOrder) : routeInput;
    let etaRoute = routeResult;
    let firstLegSec = 0;
    if (yardOrigin) {
      // Drop the yard from the stop list; its leg becomes the offset to stop 1.
      reorderedStops = reorderedStops.filter((st) => st.id !== '__yard__');
      const firstLeg = (routeResult.legs || [])[0];
      firstLegSec = firstLeg ? (firstLeg.durationInTrafficSeconds || firstLeg.durationSeconds || 0) : 0;
      startTime = new Date(startTime.getTime() + firstLegSec * 1000);
      etaRoute = { ...routeResult, legs: (routeResult.legs || []).slice(1) };
    }
    const etaResult = await calculateETAsWithDwellTimes(
      reorderedStops,
      startTime,
      etaRoute
    );
    if (!etaResult.success) return etaResult;
    // __PLAN_BASELINE__ stop 1's leg is the drive from the yard; the prep sits before it.
    if (etaResult.stops && etaResult.stops[0]) {
      etaResult.stops[0] = { ...etaResult.stops[0], legSeconds: firstLegSec, prepMinutes };
    }

    const { error: saveError } = ((opts && opts.dryRun) || !driverId) ? { error: null } : await supabase
      .from('driver_daily_routes')
      .upsert({
        driver_id: driverId,
        route_date: date,
        total_distance_meters: routeResult.totalDistanceMeters,
        total_duration_seconds: routeResult.totalDurationSeconds,
        total_dwell_seconds: etaResult.totalDwellMinutes * 60,
        estimated_start_time: etaResult.estimatedStartTime,
        estimated_end_time: etaResult.estimatedEndTime,
        load_sequence: loads.map(l => l.id),
        is_optimized: routeResult.isOptimized,
        optimization_savings_seconds: routeResult.optimizationSavingsSeconds,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'driver_id,route_date' });

    if (saveError) console.error('Error saving daily route:', saveError);

    return {
      success: true,
      driverId,
      date,
      loads,
      route: routeResult,
      stopsWithETAs: etaResult.stops,
      failedStops: failedStops || [],
      totalDistanceKm: routeResult.totalDistanceKm,
      totalDistanceMiles: routeResult.totalDistanceMiles,
      totalDriveTime: routeResult.totalDurationText,
      totalDwellTime: `${etaResult.totalDwellMinutes} min`,
      prepMinutes,
      estimatedStartTime: etaResult.estimatedStartTime,
      estimatedEndTime: etaResult.estimatedEndTime,
      isOptimized: routeResult.isOptimized,
      optimizationSavings: routeResult.optimizationSavingsSeconds 
        ? formatDuration(routeResult.optimizationSavingsSeconds) 
        : null,
    };
  } catch (error) {
    console.error('Error calculating driver daily route:', error);
    return { success: false, error: error.message };
  }
};

// __REASSIGN_ROUTE_RECONCILE__ (2026-09-12)
// When a job's driver changes (or it is unassigned), the modals updated only
// loads.driver_id -- the OLD driver's driver-app route (routes/route_loads), the
// engine's driver_daily_routes, and the stamped load_stops ETAs were left stale, so
// the losing driver's phone kept showing the moved job with wrong times. These helpers
// repair that. NOTE: persistDriverAppRoute below is a TWIN of the copy in
// RouteBuilder.js -- keep the two in sync if either changes.
const _todayISO = () => new Date().toISOString().split('T')[0];

// Mirror a computed route into the driver-app tables + stamp planned load_stops ETAs.
export const persistDriverAppRoute = async (driverId, result, planSource = 'reassign') => {
  const date = (result && result.date) || _todayISO();
  const seq = [];
  ((result && result.stopsWithETAs) || []).forEach((stp) => {
    if (stp.loadId && !seq.includes(stp.loadId)) seq.push(stp.loadId);
  });
  if (seq.length === 0) {
    ((result && result.loads) || []).forEach((l) => { if (l.id && !seq.includes(l.id)) seq.push(l.id); });
  }
  if (seq.length === 0) return;
  const durationMinutes = result.route && result.route.totalDurationSeconds != null
    ? Math.round(result.route.totalDurationSeconds / 60) : null;
  const distanceKm = result.totalDistanceKm != null
    ? Math.round(result.totalDistanceKm * 10) / 10 : null;
  const routePayload = {
    driver_id: driverId, status: 'planned', planned_date: date,
    total_stops: ((result.stopsWithETAs) || []).length || seq.length,
    estimated_distance_km: distanceKm, estimated_duration_minutes: durationMinutes,
    updated_at: new Date().toISOString(),
  };
  const { data: existing } = await supabase
    .from('routes').select('id').eq('driver_id', driverId).eq('planned_date', date).limit(1);
  let routeId;
  if (existing && existing.length > 0) {
    routeId = existing[0].id;
    await supabase.from('routes').update(routePayload).eq('id', routeId);
    await supabase.from('route_loads').delete().eq('route_id', routeId);
  } else {
    routePayload.route_number = 'RT-' + date + '-' + String(driverId).slice(0, 4).toUpperCase();
    const { data: ins, error: insErr } = await supabase
      .from('routes').insert(routePayload).select('id').single();
    if (insErr || !ins) { console.warn('driver-app route insert failed:', insErr); return; }
    routeId = ins.id;
  }
  const rows = seq.map((loadId, i) => ({ route_id: routeId, load_id: loadId, sequence_order: i + 1 }));
  const { error: rlErr } = await supabase.from('route_loads').insert(rows);
  if (rlErr) console.warn('driver-app route_loads insert failed:', rlErr);
  await stampStopPlan(result, planSource);
};

// __PLAN_BASELINE__ (2026-09-29) The plan each stop is measured against.
// Stamped ONLY when a route is built or rebuilt (Build Routes, reassignment, a job
// edit, a driver's address fix) - never from GPS / live ETAs. Columns:
//   planned_arrival_at / planned_departure_at  the planned times (timestamptz)
//   planned_dwell_min / planned_dwell_source   the stop time used and why
//   planned_leg_min                            drive INTO this stop (stop 1: from the yard)
//   planned_prep_min                           start prep before the day's first stop
//   planned_seq                                position in the driver's day
//   planned_at / plan_source                   when and by what the plan was made
// A DB trigger (trg_stops_keep_plan) throws away any change to these columns that
// does not also set a new planned_at, so nothing else can move the baseline.
// estimated_arrival/departure keep being stamped too (naive UTC, read by the
// liveEta ordering and the notify_eta_variance trigger). load_stops.dwell_minutes
// is NOT written any more: it is the human per-stop override in the dwell
// resolver, and stamping the resolved value into it froze every stop at the
// first plan's number.
// Twin copy note: RouteBuilder.js calls this too (its persistDriverAppRoute).
export const buildStopPlanRows = (result, planSource = 'build_routes', plannedAt = new Date().toISOString()) => {
  const rows = [];
  ((result && result.stopsWithETAs) || []).forEach((st, i) => {
    if (!st || !st.id || String(st.id).startsWith('__')) return;
    rows.push({
      id: st.id,
      estimated_arrival: st.estimatedArrival || null,
      estimated_departure: st.estimatedDeparture || null,
      planned_arrival_at: st.estimatedArrival || null,
      planned_departure_at: st.estimatedDeparture || null,
      planned_dwell_min: st.dwellMinutes != null ? Math.round(st.dwellMinutes) : null,
      planned_dwell_source: st.dwellSource || null,
      planned_leg_min: st.legSeconds != null ? Math.round(st.legSeconds / 60) : null,
      planned_prep_min: i === 0 ? (st.prepMinutes != null ? Math.round(st.prepMinutes) : 0) : 0,
      planned_seq: i + 1,
      planned_at: plannedAt,
      plan_source: planSource || 'build_routes',
    });
  });
  return rows;
};

export const stampStopPlan = async (result, planSource = 'build_routes') => {
  const rows = buildStopPlanRows(result, planSource);
  for (const row of rows) {
    const { id, ...payload } = row;
    try {
      const { data, error } = await supabase.from('load_stops').update(payload).eq('id', id).select('id');
      if (error) console.warn('stop plan stamp failed:', id, error);
      else if (!data || data.length === 0) console.warn('stop plan stamp updated no row (RLS?):', id);
    } catch (e) { console.warn('stop plan stamp failed:', e); }
  }
  return rows.length;
};

// Remove all trace of one load from a driver's routes, DATE-INDEPENDENT. The guaranteed
// net that makes reassignment safe even when a recompute is skipped or fails.
export const detachLoadFromDriverRoutes = async (loadId, driverId) => {
  if (!loadId || !driverId) return;
  try {
    const { data: rts } = await supabase.from('routes').select('id').eq('driver_id', driverId);
    const ids = (rts || []).map((r) => r.id);
    if (ids.length) {
      await supabase.from('route_loads').delete().eq('load_id', loadId).in('route_id', ids);
    }
    const { data: ddr } = await supabase
      .from('driver_daily_routes').select('route_date, load_sequence').eq('driver_id', driverId);
    for (const row of (ddr || [])) {
      const seq = Array.isArray(row.load_sequence) ? row.load_sequence : [];
      if (seq.includes(loadId)) {
        await supabase.from('driver_daily_routes')
          .update({ load_sequence: seq.filter((x) => x !== loadId), updated_at: new Date().toISOString() })
          .eq('driver_id', driverId).eq('route_date', row.route_date);
      }
    }
  } catch (e) { console.warn('detachLoadFromDriverRoutes failed:', e); }
};

// __OPEN_ROUTE_DETACH__ (2026-10-03) a job on an OPEN (unassigned, routes.driver_id null)
// route that is given to a driver one at a time (Load Details quick-assign, Edit, a drag
// onto a driver) leaves that open route, so it is never on two routes at once - the
// driver's own day plus the open route the office still sees. An open route left empty is
// deleted, otherwise its job count is updated (the same rule Finalize uses). Never throws.
export const detachLoadFromOpenRoutes = async (loadId) => {
  if (!loadId) return;
  try {
    const { data: rl } = await supabase.from('route_loads').select('route_id, routes(driver_id)').eq('load_id', loadId);
    const open = [...new Set((rl || []).filter((r) => r.routes && !r.routes.driver_id).map((r) => r.route_id))];
    for (const rid of open) {
      await supabase.from('route_loads').delete().eq('route_id', rid).eq('load_id', loadId);
      const { data: left } = await supabase.from('route_loads').select('load_id').eq('route_id', rid);
      if (!left || !left.length) await supabase.from('routes').delete().eq('id', rid);
      else await supabase.from('routes').update({ total_stops: left.length, updated_at: new Date().toISOString() }).eq('id', rid);
    }
  } catch (e) { console.warn('detachLoadFromOpenRoutes failed:', e); }
};

// Clear a driver's stale route for a date when they have no routable day left (0 or <2
// stops). calculateDriverDailyRoute returns success:false and clears nothing there.
export const clearDriverRouteForDate = async (driverId, date) => {
  if (!driverId) return;
  const d = date || _todayISO();
  try {
    const { data: rts } = await supabase
      .from('routes').select('id').eq('driver_id', driverId).eq('planned_date', d);
    for (const r of (rts || [])) {
      await supabase.from('route_loads').delete().eq('route_id', r.id);
      await supabase.from('routes').update({
        total_stops: 0, estimated_distance_km: 0, estimated_duration_minutes: 0,
        updated_at: new Date().toISOString(),
      }).eq('id', r.id);
    }
    await supabase.from('driver_daily_routes').delete().eq('driver_id', driverId).eq('route_date', d);
  } catch (e) { console.warn('clearDriverRouteForDate failed:', e); }
};

export const clearLoadStopETAs = async (loadId) => {
  if (!loadId) return;
  try {
    // __PLAN_BASELINE__ an unassigned / moved job has no plan until a route is rebuilt.
    await supabase.from('load_stops')
      .update({
        estimated_arrival: null, estimated_departure: null,
        planned_arrival_at: null, planned_departure_at: null, planned_dwell_min: null,
        planned_dwell_source: null, planned_leg_min: null, planned_prep_min: null,
        planned_seq: null, planned_at: null, plan_source: null,
      }).eq('load_id', loadId);
  } catch (e) { console.warn('clearLoadStopETAs failed:', e); }
};

const _recomputeOrClearDriverRoute = async (driverId, date, planSource = 'reassign', opts = {}) => {
  if (!driverId) return;
  try {
    const result = await calculateDriverDailyRoute(driverId, date, opts);
    if (result && result.success) {
      await persistDriverAppRoute(driverId, result, planSource);
    } else {
      await clearDriverRouteForDate(driverId, date);
    }
  } catch (e) { console.warn('recomputeOrClearDriverRoute failed:', e); }
};

// __ROUTE_DRAFTS__ exported for the Route Builder's finalize (old drivers' days).
export const recomputeOrClearDriverRoute = (driverId, date, planSource = 'reassign', opts = {}) =>
  _recomputeOrClearDriverRoute(driverId, date, planSource, opts);

// __REASSIGN_GUARD__ (2026-09-12) Collision guard for pulling a job off a driver.
// Terminal jobs can't be reassigned at all; in-progress jobs require an explicit
// override. Re-reads the LIVE status at call time so a stale modal copy can't slip
// an in-progress job through.
export const REASSIGN_BLOCKED_STATUSES = ['complete', 'completed', 'ready_to_bill', 'billed', 'archived', 'cancelled'];
// __STATUS_TRUTH__ (2026-10-28) at the drop-off / unloading / dropped off are in progress too (were missing: a job at its
// drop-off with no started_at could be pulled off the driver with no warning)
export const REASSIGN_INPROGRESS_STATUSES = ['assigned', 'en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'];
export const assessReassignGuard = (status, startedAt) => {
  const s = String(status || '').toLowerCase();
  if (REASSIGN_BLOCKED_STATUSES.includes(s)) {
    return { tier: 'blocked', reason: 'This job is ' + (status || 'finished') + ' - reassignment is not allowed.' };
  }
  if (REASSIGN_INPROGRESS_STATUSES.includes(s) || startedAt) {
    const justAssigned = (s === 'assigned') && !startedAt;
    const reason = justAssigned
      ? 'This job is already ASSIGNED to a driver (about to go in progress). Reassigning pulls it away from them.'
      : 'This job is IN PROGRESS' + (startedAt ? ' (started ' + fmtDbDateTime(startedAt) + ')' : ' (' + (status || 'en route') + ')') + '. Reassigning pulls it away from the active driver.';
    return { tier: 'override', reason };
  }
  return { tier: 'ok' };
};
// Re-fetch the LIVE status/started_at and classify. Fail-open on read error (never
// worse than the pre-guard behaviour, which had no check at all).
export const guardReassignAllowed = async (loadId) => {
  try {
    const { data } = await supabase.from('loads').select('status, started_at').eq('id', loadId).single();
    return assessReassignGuard(data && data.status, data && data.started_at);
  } catch (e) {
    console.warn('guardReassignAllowed read failed (allowing):', e);
    return { tier: 'ok' };
  }
};

// Orchestrator -- call AFTER loads.driver_id has been updated. Never throws.
export const reconcileRoutesAfterReassignment = async ({ loadId, oldDriverId, newDriverId, scheduledDate }) => {
  try {
    const oldD = oldDriverId || null;
    const newD = newDriverId || null;
    if (oldD && newD && oldD === newD) return;
    // __BOARD_ACTIVITY__ every driver change on the board comes through here (job window, Edit, Swap, Route Builder drag)
    if (oldD !== newD) logLoadActivity(loadId, 'driver_changed', 'driver_id', oldD, newD);
    const date = scheduledDate || _todayISO();
    if (oldD) {
      await detachLoadFromDriverRoutes(loadId, oldD);
      await _recomputeOrClearDriverRoute(oldD, date, 'reassign');
    }
    if (newD) {
      await detachLoadFromOpenRoutes(loadId);   // __OPEN_ROUTE_DETACH__
      // __PLAN_BASELINE__ drop the old driver's plan first, so a failed recompute
      // can't leave the moved job measured against someone else's day.
      await clearLoadStopETAs(loadId);
      await _recomputeOrClearDriverRoute(newD, date, 'reassign');
    } else {
      await clearLoadStopETAs(loadId);
    }
  } catch (e) { console.warn('reconcileRoutesAfterReassignment failed:', e); }
};

// __EDIT_ROUTE_REFRESH__ (2026-09-23) editing a job WITHOUT changing its driver.
// EditLoadModal deletes and re-inserts every load_stops row on save, so the
// stamped ETAs are gone and the driver's route rows still describe the old
// stops/date until someone re-runs Build Routes -- the "same-driver edit" gap
// left open by the reassignment work. Detach the load from this driver's routes
// (date-independent), then recompute the old date (if the job moved day) and the
// new one. Finished jobs are left alone. Best-effort: never throws, never blocks
// the save.
export const refreshRouteAfterEdit = async ({ loadId, driverId, status, oldDate, newDate, planSource = 'edit' }) => {
  try {
    if (!loadId || !driverId) return;
    if (REASSIGN_BLOCKED_STATUSES.includes(String(status || '').toLowerCase())) return;
    const nd = newDate || _todayISO();
    const od = oldDate || nd;
    await detachLoadFromDriverRoutes(loadId, driverId);
    if (String(od).slice(0, 10) !== String(nd).slice(0, 10)) {
      await _recomputeOrClearDriverRoute(driverId, od, planSource);
    }
    await _recomputeOrClearDriverRoute(driverId, nd, planSource);
  } catch (e) { console.warn('refreshRouteAfterEdit failed:', e); }
};

// __SWAP_SIMULATE__ (2026-09-12) Dry-run what-if for moving one job between drivers.
// Returns BOTH drivers' full day before and after, computed with the real engine
// (calculateRoute + calculateETAsWithDwellTimes) but with dryRun so NOTHING is written.
// The moved load is fetched fresh (with stops) and injected into the target's
// simulation via extraLoads; the source's via removeLoadIds. Never throws.
export const simulateSwap = async ({ loadId, sourceDriverId, targetDriverId, date }) => {
  const d = date || _todayISO();
  const out = { loadId, date: d, moved: null, before: { source: null, target: null }, after: { source: null, target: null }, error: null };
  try {
    const { data: moved, error } = await supabase.from('loads').select('*, load_stops (*)').eq('id', loadId).single();
    if (error) throw error;
    out.moved = moved;
    if (sourceDriverId) {
      out.before.source = await calculateDriverDailyRoute(sourceDriverId, d, { dryRun: true });
      out.after.source = await calculateDriverDailyRoute(sourceDriverId, d, { dryRun: true, removeLoadIds: [loadId] });
    }
    if (targetDriverId) {
      out.before.target = await calculateDriverDailyRoute(targetDriverId, d, { dryRun: true });
      out.after.target = await calculateDriverDailyRoute(targetDriverId, d, { dryRun: true, extraLoads: [moved] });
    }
  } catch (e) {
    out.error = (e && e.message) ? e.message : String(e);
  }
  return out;
};


const reorderStops = (stops, waypointOrder) => {
  if (!waypointOrder || waypointOrder.length === 0) return stops;
  
  const origin = stops[0];
  const destination = stops[stops.length - 1];
  const waypoints = stops.slice(1, -1);
  
  const reorderedWaypoints = waypointOrder.map(index => waypoints[index]);
  
  return [origin, ...reorderedWaypoints, destination];
};

/**
 * Record actual dwell time for AI tuning
 */
export const recordDwellTime = async (loadStopId, arrivalTime, departureTime, yardId = null) => {
  try {
    const arrival = new Date(arrivalTime);
    const departure = new Date(departureTime);
    const actualDwellMinutes = Math.round((departure - arrival) / 60000);

    const { data: stop } = await supabase
      .from('load_stops')
      .select('load_id, preset_dwell_minutes, address')
      .eq('id', loadStopId)
      .single();

    const presetDwellMinutes = stop?.preset_dwell_minutes || 30;
    const varianceMinutes = actualDwellMinutes - presetDwellMinutes;

    // Samples go to dwell_time_history - the one log the learning triggers read
    // (sql/dwell_learning.sql). dwell_time_records is deprecated: it had this one
    // writer and no readers at all.
    const { data, error } = await supabase
      .from('dwell_time_history')
      .insert({
        stop_id: loadStopId,
        load_id: stop?.load_id,
        location_address: stop?.address,
        arrival_time: arrivalTime,
        departure_time: departureTime,
        actual_dwell_minutes: actualDwellMinutes,
        expected_dwell_minutes: presetDwellMinutes,
        variance_minutes: varianceMinutes,
        source: 'manual',
      })
      .select()
      .single();

    if (error) throw error;

    await supabase
      .from('load_stops')
      .update({
        actual_arrival_time: arrivalTime,
        actual_departure_time: departureTime,
      })
      .eq('id', loadStopId);

    return { success: true, data, actualDwellMinutes, varianceMinutes };
  } catch (error) {
    console.error('Error recording dwell time:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get or create a yard from an address
 */
export const getOrCreateYard = async (address, name = null) => {
  try {
    const { data: existingYard } = await supabase
      .from('yards')
      .select('*')
      .ilike('address', `%${address}%`)
      .limit(1)
      .single();

    if (existingYard) return { success: true, yard: existingYard, isNew: false };

    const geocoded = await geocodeAddress(address);
    
    const { data: newYard, error } = await supabase
      .from('yards')
      .insert({
        name: name || address.split(',')[0],
        address,
        latitude: geocoded.lat,
        longitude: geocoded.lng,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, yard: newYard, isNew: true };
  } catch (error) {
    console.error('Error getting/creating yard:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get yard dwell time statistics
 */
export const getYardDwellStats = async (yardId = null) => {
  try {
    let query = supabase.from('yard_dwell_stats').select('*');
    
    if (yardId) {
      query = query.eq('id', yardId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching yard dwell stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create billing distance record for a completed load
 */
export const createBillingRecord = async (loadId, actualDistanceMeters = null, actualDurationSeconds = null) => {
  try {
    const { data: load } = await supabase
      .from('loads')
      .select(`
        *,
        route_calculations (*)
      `)
      .eq('id', loadId)
      .single();

    if (!load) throw new Error('Load not found');

    const routeCalc = load.route_calculations?.[0];
    
    const { data, error } = await supabase
      .from('billing_distance_records')
      .insert({
        load_id: loadId,
        driver_id: load.driver_id,
        truck_id: load.truck_id,
        calculated_distance_meters: routeCalc?.total_distance_meters,
        actual_distance_meters: actualDistanceMeters || routeCalc?.total_distance_meters,
        calculated_duration_seconds: routeCalc?.total_duration_seconds,
        actual_duration_seconds: actualDurationSeconds || routeCalc?.total_duration_seconds,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error creating billing record:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all yards
 */
export const getYards = async () => {
  try {
    const { data, error } = await supabase
      .from('yards')
      .select('*')
      .eq('is_active', true)
      .order('name');
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching yards:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update yard preset dwell time
 */
export const updateYardDwellTime = async (yardId, presetDwellMinutes) => {
  try {
    const { data, error } = await supabase
      .from('yards')
      .update({ preset_dwell_minutes: presetDwellMinutes, updated_at: new Date().toISOString() })
      .eq('id', yardId)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating yard dwell time:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a detour to a driver's daily route
 * Validates against critical time windows before allowing
 */
export const addDetour = async (driverId, dailyRouteId, detour) => {
  try {
    const { data: dailyRoute } = await supabase
      .from('driver_daily_routes')
      .select('*')
      .eq('id', dailyRouteId)
      .single();

    if (!dailyRoute) {
      return { success: false, error: 'Daily route not found' };
    }

    const geocoded = detour.address ? await geocodeAddress(detour.address) : null;

    const validation = await validateDetourAgainstCriticalTimes(
      driverId,
      dailyRoute.route_date,
      detour,
      detour.insert_after_stop_id
    );

    if (!validation.canAdd && validation.blockingStops.length > 0) {
      return {
        success: false,
        error: 'Detour would cause late arrival at critical time window',
        blockingStops: validation.blockingStops,
        impactedETAs: validation.impactedETAs,
      };
    }

    const { data, error } = await supabase
      .from('driver_detours')
      .insert({
        driver_id: driverId,
        daily_route_id: dailyRouteId,
        detour_type: detour.type || 'other',
        address: detour.address,
        latitude: geocoded?.lat,
        longitude: geocoded?.lng,
        planned_duration_minutes: detour.durationMinutes || 15,
        insert_after_stop_id: detour.insert_after_stop_id,
        insert_sequence: detour.insert_sequence,
        notes: detour.notes,
        impacts_critical_times: validation.hasWarnings,
        status: 'planned',
      })
      .select()
      .single();

    if (error) throw error;

    const updatedRoute = await calculateDriverDailyRoute(driverId, dailyRoute.route_date);

    return {
      success: true,
      detour: data,
      updatedRoute,
      warnings: validation.warnings,
    };
  } catch (error) {
    console.error('Error adding detour:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Validate a detour against critical time windows
 */
export const validateDetourAgainstCriticalTimes = async (driverId, date, detour, insertAfterStopId) => {
  try {
    const { data: loads } = await supabase
      .from('loads')
      .select(`
        *,
        load_stops (*)
      `)
      .eq('driver_id', driverId)
      .eq('scheduled_date', date)
      .not('status', 'in', '("complete","cancelled")');

    if (!loads || loads.length === 0) {
      return { canAdd: true, blockingStops: [], warnings: [], impactedETAs: [] };
    }

    const criticalStops = [];
    loads.forEach(load => {
      (load.load_stops || []).forEach(stop => {
        if (stop.time_critical && (stop.time_window_start || stop.time_window_end)) {
          criticalStops.push({
            ...stop,
            loadNumber: load.load_number,
          });
        }
      });
    });

    if (criticalStops.length === 0) {
      return { canAdd: true, blockingStops: [], warnings: [], hasWarnings: false };
    }

    const detourDurationSeconds = (detour.durationMinutes || 15) * 60;
    const estimatedDetourTravelSeconds = 600;
    const totalDelaySeconds = detourDurationSeconds + estimatedDetourTravelSeconds;

    const blockingStops = [];
    const warnings = [];
    const impactedETAs = [];

    for (const stop of criticalStops) {
      if (stop.estimated_arrival_time && stop.time_window_end) {
        const currentETA = new Date(stop.estimated_arrival_time);
        const newETA = new Date(currentETA.getTime() + (totalDelaySeconds * 1000));
        const windowEnd = parseWindowTime(stop.time_window_end);   // __TZ_WINDOWS__ Toronto wall clock

        if (newETA > windowEnd) {
          const lateByMinutes = Math.round((newETA - windowEnd) / 60000);
          blockingStops.push({
            ...stop,
            lateByMinutes,
            message: `Would arrive ${lateByMinutes} min late at ${stop.address}`,
          });
        } else {
          const bufferMinutes = Math.round((windowEnd - newETA) / 60000);
          if (bufferMinutes < 15) {
            warnings.push({
              stop,
              bufferMinutes,
              message: `Only ${bufferMinutes} min buffer before deadline at ${stop.address}`,
            });
          }
          impactedETAs.push({
            stopId: stop.id,
            originalETA: currentETA.toISOString(),
            newETA: newETA.toISOString(),
            delayMinutes: Math.round(totalDelaySeconds / 60),
          });
        }
      }
    }

    return {
      canAdd: blockingStops.length === 0,
      blockingStops,
      warnings,
      impactedETAs,
      hasWarnings: warnings.length > 0,
    };
  } catch (error) {
    console.error('Error validating detour:', error);
    return { canAdd: false, error: error.message };
  }
};

/**
 * Get detours for a driver's daily route
 */
export const getDetours = async (dailyRouteId) => {
  try {
    const { data, error } = await supabase
      .from('driver_detours')
      .select('*')
      .eq('daily_route_id', dailyRouteId)
      .order('insert_sequence');

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching detours:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove a detour
 */
export const removeDetour = async (detourId) => {
  try {
    const { data: detour } = await supabase
      .from('driver_detours')
      .select('daily_route_id, driver_id')
      .eq('id', detourId)
      .single();

    const { error } = await supabase
      .from('driver_detours')
      .delete()
      .eq('id', detourId);

    if (error) throw error;

    if (detour) {
      const { data: dailyRoute } = await supabase
        .from('driver_daily_routes')
        .select('route_date')
        .eq('id', detour.daily_route_id)
        .single();

      if (dailyRoute) {
        await calculateDriverDailyRoute(detour.driver_id, dailyRoute.route_date);
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error removing detour:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get common detour types
 */
export const getDetourTypes = () => [
  { value: 'fuel', label: 'Fuel Stop', icon: '⛽', defaultMinutes: 15 },
  { value: 'break', label: 'Break', icon: '☕', defaultMinutes: 15 },
  { value: 'meal', label: 'Meal Break', icon: '🍔', defaultMinutes: 30 },
  { value: 'rest', label: 'Rest Stop', icon: '🛏️', defaultMinutes: 30 },
  { value: 'maintenance', label: 'Quick Maintenance', icon: '🔧', defaultMinutes: 20 },
  { value: 'weigh_station', label: 'Weigh Station', icon: '⚖️', defaultMinutes: 10 },
  { value: 'other', label: 'Other', icon: '📍', defaultMinutes: 15 },
];

/**
 * Check if route can meet all critical time windows
 */
export const checkCriticalTimeCompliance = async (driverId, date) => {
  try {
    const routeResult = await calculateDriverDailyRoute(driverId, date);
    
    if (!routeResult.success) {
      return { success: false, error: routeResult.error || routeResult.message };
    }

    const violations = [];
    const warnings = [];

    for (const stop of routeResult.stopsWithETAs || []) {
      if (stop.time_critical) {
        const timeWindowCheck = stop.isWithinTimeWindow;
        
        if (timeWindowCheck.status === 'late') {
          violations.push({
            stop,
            type: 'late',
            message: `Will arrive ${timeWindowCheck.lateMinutes} min late at ${stop.address}`,
            severity: 'critical',
          });
        } else if (timeWindowCheck.status === 'early' && stop.stop_type === 'pickup') {
          violations.push({
            stop,
            type: 'early',
            message: `Will arrive ${timeWindowCheck.earlyMinutes} min early at ${stop.address}`,
            severity: 'warning',
          });
        }
      }
    }

    return {
      success: true,
      isCompliant: violations.filter(v => v.severity === 'critical').length === 0,
      violations,
      warnings,
      route: routeResult,
    };
  } catch (error) {
    console.error('Error checking critical time compliance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Suggest optimal detour locations that won't impact critical times
 */
export const suggestDetourWindows = async (driverId, date, detourDurationMinutes = 15) => {
  try {
    const routeResult = await calculateDriverDailyRoute(driverId, date);
    
    if (!routeResult.success) {
      return { success: false, error: routeResult.error || routeResult.message };
    }

    const stops = routeResult.stopsWithETAs || [];
    const suggestions = [];

    for (let i = 0; i < stops.length - 1; i++) {
      const currentStop = stops[i];
      const nextStop = stops[i + 1];
      
      const currentDeparture = new Date(currentStop.estimatedDeparture);
      const nextArrival = new Date(nextStop.estimatedArrival);
      const travelTimeMs = nextArrival - currentDeparture;
      
      let availableBufferMs = 0;
      
      if (nextStop.time_critical && nextStop.time_window_end) {
        // nextArrival is a computed instant; __TZ_WINDOWS__ time_window_end is Toronto
        // wall clock, made a real instant by parseWindowTime (DST-safe).
        const windowEnd = parseWindowTime(nextStop.time_window_end);
        availableBufferMs = windowEnd ? (windowEnd - nextArrival) : 30 * 60 * 1000;
      } else {
        availableBufferMs = 30 * 60 * 1000;
      }

      const detourTimeMs = detourDurationMinutes * 60 * 1000;
      const canFitDetour = availableBufferMs >= detourTimeMs;

      if (canFitDetour) {
        suggestions.push({
          afterStop: currentStop,
          beforeStop: nextStop,
          availableMinutes: Math.floor(availableBufferMs / 60000),
          travelTimeMinutes: Math.floor(travelTimeMs / 60000),
          recommended: availableBufferMs >= detourTimeMs * 2,
          message: `${Math.floor(availableBufferMs / 60000)} min available between ${currentStop.address.split(',')[0]} and ${nextStop.address.split(',')[0]}`,
        });
      }
    }

    return {
      success: true,
      suggestions: suggestions.sort((a, b) => b.availableMinutes - a.availableMinutes),
      requestedDuration: detourDurationMinutes,
    };
  } catch (error) {
    console.error('Error suggesting detour windows:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// DRIVER PRESET LOCATIONS
// ============================================

export const getDriverPresetLocations = async (driverId, locationType = null) => {
  try {
    let query = supabase
      .from('driver_preset_locations')
      .select('*')
      .eq('driver_id', driverId)
      .eq('is_active', true)
      .order('is_favorite', { ascending: false })
      .order('use_count', { ascending: false });

    if (locationType) {
      query = query.eq('location_type', locationType);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, locations: data || [] };
  } catch (error) {
    console.error('Error fetching driver preset locations:', error);
    return { success: false, error: error.message };
  }
};

export const addDriverPresetLocation = async (driverId, location) => {
  try {
    const geocoded = location.address ? await geocodeAddress(location.address) : null;

    const { data, error } = await supabase
      .from('driver_preset_locations')
      .insert({
        driver_id: driverId,
        name: location.name,
        address: location.address,
        latitude: geocoded?.lat || location.latitude,
        longitude: geocoded?.lng || location.longitude,
        location_type: location.location_type || location.type,
        typical_duration_minutes: location.typical_duration_minutes || 15,
        notes: location.notes,
        is_favorite: location.is_favorite || false,
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, location: data };
  } catch (error) {
    console.error('Error adding driver preset location:', error);
    return { success: false, error: error.message };
  }
};

export const updateDriverPresetLocation = async (locationId, updates) => {
  try {
    const { data, error } = await supabase
      .from('driver_preset_locations')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', locationId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, location: data };
  } catch (error) {
    console.error('Error updating driver preset location:', error);
    return { success: false, error: error.message };
  }
};

export const deleteDriverPresetLocation = async (locationId) => {
  try {
    const { error } = await supabase
      .from('driver_preset_locations')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', locationId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting driver preset location:', error);
    return { success: false, error: error.message };
  }
};

export const togglePresetLocationFavorite = async (locationId) => {
  try {
    const { data: current } = await supabase
      .from('driver_preset_locations')
      .select('is_favorite')
      .eq('id', locationId)
      .single();

    const { data, error } = await supabase
      .from('driver_preset_locations')
      .update({
        is_favorite: !current?.is_favorite,
        updated_at: new Date().toISOString(),
      })
      .eq('id', locationId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, location: data };
  } catch (error) {
    console.error('Error toggling preset location favorite:', error);
    return { success: false, error: error.message };
  }
};

export const recordPresetLocationUse = async (locationId) => {
  try {
    const { data: current } = await supabase
      .from('driver_preset_locations')
      .select('use_count')
      .eq('id', locationId)
      .single();

    const { error } = await supabase
      .from('driver_preset_locations')
      .update({
        use_count: (current?.use_count || 0) + 1,
        last_used_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', locationId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error recording preset location use:', error);
    return { success: false, error: error.message };
  }
};

export const addDetourFromPreset = async (driverId, dailyRouteId, presetLocationId, insertAfterStopId = null) => {
  try {
    const { data: preset } = await supabase
      .from('driver_preset_locations')
      .select('*')
      .eq('id', presetLocationId)
      .single();

    if (!preset) {
      return { success: false, error: 'Preset location not found' };
    }

    const detourResult = await addDetour(driverId, dailyRouteId, {
      detour_type: preset.location_type,
      address: preset.address,
      latitude: preset.latitude,
      longitude: preset.longitude,
      planned_duration_minutes: preset.typical_duration_minutes,
      notes: `From preset: ${preset.name}`,
      insert_after_stop_id: insertAfterStopId,
    });

    if (detourResult.success) {
      await recordPresetLocationUse(presetLocationId);
    }

    return detourResult;
  } catch (error) {
    console.error('Error adding detour from preset:', error);
    return { success: false, error: error.message };
  }
};

export const getPresetLocationTypes = () => {
  return [
    { value: 'fuel', label: 'Fuel Stop', icon: '⛽' },
    { value: 'break', label: 'Break Spot', icon: '☕' },
    { value: 'meal', label: 'Meal Stop', icon: '🍔' },
    { value: 'rest', label: 'Rest Area', icon: '🛏️' },
    { value: 'maintenance', label: 'Maintenance Shop', icon: '🔧' },
    { value: 'weigh_station', label: 'Weigh Station', icon: '⚖️' },
    { value: 'parking', label: 'Truck Parking', icon: '🅿️' },
    { value: 'other', label: 'Other', icon: '📍' },
  ];
};

const routingEngineService = {
  getRoutingConfig,
  saveRoutingConfig,
  isGoogleMapsConfigured,
  geocodeAddress,
  calculateRoute,
  calculateETAsWithDwellTimes,
  calculateDriverDailyRoute,
  recordDwellTime,
  getOrCreateYard,
  getYardDwellStats,
  createBillingRecord,
  getYards,
  updateYardDwellTime,
  addDetour,
  validateDetourAgainstCriticalTimes,
  getDetours,
  removeDetour,
  getDetourTypes,
  checkCriticalTimeCompliance,
  suggestDetourWindows,
  getDriverPresetLocations,
  addDriverPresetLocation,
  updateDriverPresetLocation,
  deleteDriverPresetLocation,
  togglePresetLocationFavorite,
  recordPresetLocationUse,
  addDetourFromPreset,
  getPresetLocationTypes,
};

export default routingEngineService;
